/**
 * Resolves whether the graph explorer runs in "showcase" mode — breadth-first
 * selection that demonstrates how much of the graph is connected, rather than
 * optimising every slot for the viewer's personal relevance.
 *
 * Two independent triggers, either of which is sufficient:
 *
 *   1. The `twg-microsite-graph-showcase` feature gate, for a normal rollout.
 *   2. A `?graph=showcase` URL parameter, so a live demo does not depend on
 *      gate propagation timing.
 *
 * The URL parameter is *consumed*: `consumeShowcaseUrlOverride` latches the
 * request for the rest of the tab's session and strips the parameter back out of
 * the address bar, so the URL on screen stays clean while presenting. From that
 * point on the override behaves exactly like a gate that happens to be on.
 *
 * `?graph=default` is the counterpart, and the only way back out: it clears a
 * latched override — and beats the gate, so the ordinary graph can be shown even
 * once showcase mode has rolled out.
 *
 * Showcase mode changes slot allocation, so the value is resolved once per
 * explorer mount; flipping it mid-session would reshuffle the graph under the
 * viewer.
 */
import { getDocument } from '@atlaskit/browser-apis';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import { captureLandingHash, extractHashQuery } from '../common/deep-link';

export const SHOWCASE_FEATURE_GATE = 'twg-microsite-graph-showcase';
export const SHOWCASE_QUERY_PARAM = 'graph';
export const SHOWCASE_QUERY_VALUE = 'showcase';

/**
 * Where a consumed override is remembered.
 *
 * `sessionStorage`, not `localStorage`: the override should survive reloads and
 * in-tab navigation during a demo, and disappear when the tab does. A value that
 * outlived the browser session would leave someone in showcase mode weeks later
 * with nothing in the URL to explain why.
 */
const SHOWCASE_STORAGE_KEY = 'twg-microsite-graph-showcase';

/** What the current URL asks for, if anything. */
type UrlRequest = 'showcase' | 'default' | 'absent';

/**
 * The consumed override, once resolved. `null` means "nothing has claimed a
 * value", which is different from `false` ("explicitly asked for the ordinary
 * graph") — only the latter outranks the feature gate.
 */
let latchedOverride: boolean | null = null;

const stripLeadingQuestion = (search: string): string =>
	search.startsWith('?') ? search.slice(1) : search;

function readSessionOverride(): boolean | null {
	try {
		const raw = getDocument()?.defaultView?.sessionStorage?.getItem(SHOWCASE_STORAGE_KEY);
		if (raw === 'true') {
			return true;
		}
		if (raw === 'false') {
			return false;
		}
	} catch {
		// Storage can throw outright (Safari private browsing, blocked cookies).
		// An unavailable store only costs the override its persistence across
		// reloads, so there is nothing to handle.
	}
	return null;
}

function writeSessionOverride(value: boolean | null): void {
	try {
		const storage = getDocument()?.defaultView?.sessionStorage;
		if (!storage) {
			return;
		}
		if (value === null) {
			storage.removeItem(SHOWCASE_STORAGE_KEY);
		} else {
			storage.setItem(SHOWCASE_STORAGE_KEY, String(value));
		}
	} catch {
		// See `readSessionOverride`.
	}
}

/**
 * Reads the `graph` parameter from the URL.
 *
 * The microsite serves deep links behind the hash (`#your-graph?graph=showcase`),
 * so `window.location.search` alone is not enough — the hash fragment's query
 * part is checked as well. A real `?query` wins, matching `getAttribution`.
 *
 * SSR-safe: returns `'absent'` without `window`.
 */
function readUrlRequest(): UrlRequest {
	if (typeof window === 'undefined') {
		return 'absent';
	}
	const parts = [
		stripLeadingQuestion(window.location.search ?? ''),
		extractHashQuery(captureLandingHash()),
	].filter(Boolean);
	if (parts.length === 0) {
		return 'absent';
	}
	const value = new URLSearchParams(parts.join('&')).get(SHOWCASE_QUERY_PARAM);
	if (value === null) {
		return 'absent';
	}
	return value.toLowerCase() === SHOWCASE_QUERY_VALUE ? 'showcase' : 'default';
}

/**
 * True when the URL *currently* asks for showcase mode.
 *
 * Note this reads the live URL, so it goes false once
 * {@link consumeShowcaseUrlOverride} has stripped the parameter. Callers deciding
 * how to render should use {@link isShowcaseModeEnabled} instead.
 */
export function isShowcaseRequestedByUrl(): boolean {
	return readUrlRequest() === 'showcase';
}

/**
 * Removes one parameter from a raw query string, leaving every other pair byte
 * for byte as it arrived.
 *
 * Deliberately not `URLSearchParams` round-tripping: that re-encodes the whole
 * string, and this runs over the fragment that also carries acquisition UTMs.
 * Rewriting those is a needless risk when the job is to delete one pair.
 */
function removeQueryParam(query: string, name: string): string {
	const kept = query.split('&').filter((pair) => pair !== '' && pair.split('=')[0] !== name);
	return kept.join('&');
}

/**
 * Rewrites the address bar without the `graph` parameter, preserving the path,
 * any other query parameters, and the hash's own section token.
 *
 * Uses `replaceState` so no history entry is added — pressing Back should leave
 * the site rather than restore a URL the visitor never typed. There is no router
 * to keep in sync (the microsite has none; `clearHash` mutates history the same
 * way).
 */
function stripShowcaseParamFromUrl(): void {
	const view = getDocument()?.defaultView;
	if (!view) {
		return;
	}
	const { pathname, search, hash } = view.location;

	const nextSearchQuery = removeQueryParam(
		stripLeadingQuestion(search ?? ''),
		SHOWCASE_QUERY_PARAM,
	);
	const nextSearch = nextSearchQuery ? `?${nextSearchQuery}` : '';

	let nextHash = hash ?? '';
	const hashQueryIdx = nextHash.indexOf('?');
	if (hashQueryIdx !== -1) {
		const token = nextHash.slice(0, hashQueryIdx);
		const nextHashQuery = removeQueryParam(nextHash.slice(hashQueryIdx + 1), SHOWCASE_QUERY_PARAM);
		nextHash = nextHashQuery ? `${token}?${nextHashQuery}` : token;
	}

	if (nextSearch === (search ?? '') && nextHash === (hash ?? '')) {
		return;
	}

	// Captured before the rewrite so a `hashchange` listener diffing
	// `oldURL`/`newURL` sees the change, matching `clearHash`.
	const oldURL = view.location.href;
	view.history.replaceState(view.history.state, '', `${pathname}${nextSearch}${nextHash}`);

	if (nextHash !== (hash ?? '')) {
		view.dispatchEvent(
			new view.HashChangeEvent('hashchange', { oldURL, newURL: view.location.href }),
		);
	}
}

/**
 * Consumes `?graph=…` from the landing URL: latches what it asked for, then
 * removes it from the address bar. Call once during app bootstrap, before the
 * explorer mounts.
 *
 * Idempotent — a second call finds nothing left in the URL and keeps the latched
 * value.
 *
 * Returns the resolved override (`null` when the URL said nothing and no earlier
 * override is stored), mostly so callers can log or assert on it.
 */
export function consumeShowcaseUrlOverride(): boolean | null {
	const request = readUrlRequest();

	if (request === 'absent') {
		// Nothing to consume. Rehydrate whatever an earlier load latched so a
		// reload of the (now clean) URL stays in showcase mode.
		latchedOverride = latchedOverride ?? readSessionOverride();
		return latchedOverride;
	}

	latchedOverride = request === 'showcase';
	// `?graph=default` clears the stored override rather than persisting `false`,
	// so a later `?graph=showcase` is not fighting a sticky opt-out. It still
	// latches `false` in memory, which is what beats the gate for this page view.
	writeSessionOverride(latchedOverride ? true : null);
	stripShowcaseParamFromUrl();
	return latchedOverride;
}

/**
 * Resolves showcase mode from the consumed URL override, then the feature gate.
 *
 * The URL is still read as a last resort so hosts that never call
 * {@link consumeShowcaseUrlOverride} (Storybook, tests, any other embedder)
 * behave as they did before the parameter became consumable.
 */
export function isShowcaseModeEnabled(): boolean {
	const override = latchedOverride ?? readSessionOverride();
	if (override !== null) {
		return override;
	}
	if (isShowcaseRequestedByUrl()) {
		return true;
	}
	return fg(SHOWCASE_FEATURE_GATE);
}
